import { useFavoritesContext } from "@/contexts";
import { MuxFavorite, MuxInfoCircle } from "@/Icons";
import { DropdownMenuItem } from "@/shared-components";
import styles from "./FavoriteContextMenuItem.module.scss";
import { ITreeItem } from "@/interfaces";
import { ToolTip } from "@/shared-components";
import { useEffect, useRef, useState } from "react";
import { formatTranslation, getTranslation } from "@/utils";

interface FavoriteContextMenuItemProps {
  isFavorite: boolean;
  data: ITreeItem;
}

export const FavoriteContextMenuItem = ({
  isFavorite,
  data,
}: FavoriteContextMenuItemProps) => {
  const { favorites, toggleFavorite, maxNumberOfFavorites, topLevelCount } =
    useFavoritesContext();

  const [pos, setPos] = useState({ left: 0, top: 0 });
  const [hover, setHover] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const isLimitReached = topLevelCount(favorites) >= maxNumberOfFavorites;

  const isDisabled = !isFavorite && isLimitReached;

  const getErrors = () => {
    if (isLimitReached) {
      return [
        formatTranslation(
          "filters-tree.messages.max-favorites-allowed",
          `Maximum favorites allowed: ${maxNumberOfFavorites}`,
          { max: maxNumberOfFavorites },
        ),
      ];
    }

    return [];
  };
  const errors = getErrors();

  useEffect(() => {
    if (tooltipRef?.current && errors.length) {
      const rect = tooltipRef.current.getBoundingClientRect();
      setPos({ left: rect?.left - 90, top: rect?.top - 40 });
    }
  }, [tooltipRef?.current, isMenuOpen]);

  const handleToggleFavorite = () => {
    if (!isFavorite && isLimitReached) {
      return;
    }

    toggleFavorite(data);
  };

  return (
    <DropdownMenuItem
      onClick={handleToggleFavorite}
      icon={<MuxFavorite />}
      disabled={isDisabled}
    >
      <div className={styles.addToFavorite}>
        {isFavorite
          ? getTranslation(
              "filters-tree.context-menu.remove-from-favorites",
              "Remove From Favorites",
            )
          : getTranslation(
              "filters-tree.context-menu.add-to-favorites",
              "Add to Favorites",
            )}
        {errors.length && !isFavorite ? (
          <div ref={tooltipRef} className={styles.addToFavoriteInfo}>
            {hover && (
              // ToDo: fix typescript error
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              <ToolTip left={pos?.left} top={pos?.top} messages={errors} />
            )}
            <span
              onMouseEnter={() => {
                setIsMenuOpen(true);
                setHover(true);
              }}
              onMouseLeave={() => setHover(false)}
            >
              <MuxInfoCircle />
            </span>
          </div>
        ) : null}
      </div>
    </DropdownMenuItem>
  );
};
